import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * The connector is a laid-out sibling in the marker column and grows with
 * flex-1, so it stays attached however the content reflows. Positioning it
 * absolutely against a fixed height only ever lines up for one icon size and
 * one line of text.
 */
const TimelineItemContext = React.createContext(false);

const Timeline = React.forwardRef<HTMLOListElement, React.ComponentPropsWithoutRef<'ol'>>(
  ({ className, ...props }, ref) => (
    <ol ref={ref} data-slot="timeline" className={cn('flex flex-col', className)} {...props} />
  ),
);
Timeline.displayName = 'Timeline';

interface TimelineItemProps extends React.ComponentPropsWithoutRef<'li'> {
  isLast?: boolean;
}

const TimelineItem = React.forwardRef<HTMLLIElement, TimelineItemProps>(
  ({ isLast = false, className, children, ...props }, ref) => (
    <TimelineItemContext.Provider value={isLast}>
      <li
        ref={ref}
        data-slot="timeline-item"
        className={cn('grid grid-cols-[22px_1fr] gap-x-3', className)}
        {...props}
      >
        {children}
      </li>
    </TimelineItemContext.Provider>
  ),
);
TimelineItem.displayName = 'TimelineItem';

const TimelineMarker = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, children, ...props }, ref) => {
    const isLast = React.useContext(TimelineItemContext);

    return (
      <div
        ref={ref}
        data-slot="timeline-marker"
        className={cn('flex flex-col items-center', className)}
        {...props}
      >
        {children}
        {!isLast && (
          <span
            data-testid="timeline-connector"
            data-slot="timeline-connector"
            aria-hidden="true"
            className="mt-1.5 w-px flex-1 rounded-full bg-gradient-to-b from-border to-border/40"
          />
        )}
      </div>
    );
  },
);
TimelineMarker.displayName = 'TimelineMarker';

const TimelineContent = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>(
  ({ className, ...props }, ref) => {
    const isLast = React.useContext(TimelineItemContext);

    return (
      <div
        ref={ref}
        data-slot="timeline-content"
        className={cn('min-w-0', !isLast && 'pb-5', className)}
        {...props}
      />
    );
  },
);
TimelineContent.displayName = 'TimelineContent';

export { Timeline, TimelineItem, TimelineMarker, TimelineContent };
